import { apiFetch } from './client';

export interface CustomWebhook {
  id: string; name: string; description: string; url: string; method: string;
  has_headers: boolean; body_schema: Record<string, unknown>; enabled: boolean;
}
export interface McpServerDto {
  id: string; name: string; url: string; transport: string;
  has_auth: boolean; enabled: boolean; last_ok_at: string | null; last_error: string | null;
}
export interface SkillBundleDto {
  id: string; name: string; description: string | null; instructions: string | null;
  tool_refs: string[]; file_ids: string[]; enabled: boolean;
}
export interface InboundWebhookDto {
  id: string; name: string; agent_id: string | null; rate_limit_per_min: number;
  enabled: boolean; has_signing_secret: boolean; url: string; token?: string | null;
}
export interface InboundCreated extends InboundWebhookDto { token: string; }
export interface SkillFileDto {
  id: string; name: string; original_name?: string | null; mime_type: string;
  size_bytes: number; text_chars: number | null; has_text: boolean;
}
export interface AgentEnabled {
  enabled_webhook_ids: string[]; enabled_mcp_ids: string[]; enabled_skill_ids: string[];
}
export interface McpToolInfo { name: string; description: string; }
export interface McpTestResult { ok: boolean; error: string | null; tools: string[]; toolDetails?: McpToolInfo[]; }
export interface RemoteMcpProviderDto {
  id: string; name: string; description: string; category: string; icon: string;
  url: string; needsToken: boolean; sampleTools: string[];
  connected: boolean; serverId: string | null;
}
export interface McpCatalogResponse { demoMode: boolean; providers: RemoteMcpProviderDto[]; }
export interface McpCatalogConnectResult { server: McpServerDto; demo: boolean; tools: string[]; already?: boolean; }
export type EnableKind = 'webhook' | 'mcp' | 'skill';

export type ConnectionStatus = "connected" | "demo" | "error" | "pending";
export interface IntegrationConnectionDto {
  id: string; tenantId: number; provider: string; status: ConnectionStatus;
  accountLabel: string | null; externalAccountId?: string | null; scopes: string[]; meta: Record<string, unknown>;
  hasToken: boolean; expiresAt: string | null; lastError: string | null; enabled?: boolean;
}
export interface CatalogProviderDto {
  id: string; name: string; description: string; category: string; icon: string;
  authType: "oauth" | "mcp_remote" | "custom_mcp" | "token" | "composio"; status: "available" | "coming_soon";
  oauthProvider?: string; scopes?: string[]; tools?: string[]; setupNote?: string[];
  tokenAuthSupported?: boolean; advancedApiSupported?: boolean;
  canConnect: boolean; realOAuth: boolean; connection: IntegrationConnectionDto | null; connections?: IntegrationConnectionDto[];
}
export interface CatalogResponse { demoMode: boolean; providers: CatalogProviderDto[]; }
export interface ConnectResult { mode: "oauth" | "demo" | "composio"; authorizeUrl?: string; connection?: IntegrationConnectionDto; connectionId?: string; connectedAccountId?: string; }
export interface AgentConnections { enabledConnectionIds: string[]; connections: IntegrationConnectionDto[]; }

export interface TgResourceDto {
  id: string; connectionId: string; mode: 'platform' | 'custom'; chatType: string;
  title: string | null; username: string | null;
  status: 'pending' | 'ready' | 'no_rights' | 'bot_removed'; canPost: boolean; confirmed: boolean; lastError: string | null;
}
export interface TgConnectionDto {
  id: string; mode: 'platform' | 'custom'; status: string; accountLabel: string | null; botUsername: string | null; lastError: string | null;
}
export interface TgOverview {
  platformBotConfigured: boolean; platformBotUsername: string | null;
  connections: TgConnectionDto[]; resources: TgResourceDto[];
}
export interface TgConnectPlatform { ok: boolean; connectionId: string; botUsername: string; deepLink: string; nonce: string; }

const j = (d: unknown) => ({ body: JSON.stringify(d) });

export const integrationsApi = {
  // ---- outbound webhooks ----
  listWebhooks: () => apiFetch<CustomWebhook[]>('/api/integrations/webhooks'),
  createWebhook: (d: Partial<CustomWebhook> & { headers?: Record<string, string> }) =>
    apiFetch<CustomWebhook>('/api/integrations/webhooks', { method: 'POST', ...j(d) }),
  updateWebhook: (id: string, d: Record<string, unknown>) =>
    apiFetch<CustomWebhook>(`/api/integrations/webhooks/${id}`, { method: 'PATCH', ...j(d) }),
  deleteWebhook: (id: string) =>
    apiFetch<void>(`/api/integrations/webhooks/${id}`, { method: 'DELETE' }),

  // ---- MCP servers ----
  listMcp: () => apiFetch<McpServerDto[]>('/api/integrations/mcp'),
  createMcp: (d: { name: string; url: string; transport?: string; authToken?: string }) =>
    apiFetch<McpServerDto>('/api/integrations/mcp', { method: 'POST', ...j(d) }),
  updateMcp: (id: string, d: Record<string, unknown>) =>
    apiFetch<McpServerDto>(`/api/integrations/mcp/${id}`, { method: 'PATCH', ...j(d) }),
  deleteMcp: (id: string) => apiFetch<void>(`/api/integrations/mcp/${id}`, { method: 'DELETE' }),
  testMcp: (id: string) => apiFetch<McpTestResult>(`/api/integrations/mcp/${id}/test`, { method: 'POST', body: '{}' }),
  getMcpCatalog: () => apiFetch<McpCatalogResponse>('/api/integrations/mcp-catalog'),
  connectMcpCatalog: (id: string, authToken?: string) =>
    apiFetch<McpCatalogConnectResult>(`/api/integrations/mcp-catalog/${id}/connect`, { method: 'POST', ...j(authToken ? { authToken } : {}) }),

  // ---- skill bundles ----
  listSkills: () => apiFetch<SkillBundleDto[]>('/api/integrations/skills'),
  createSkill: (d: { name: string; description?: string; instructions?: string; tool_refs?: string[]; file_ids?: string[] }) =>
    apiFetch<SkillBundleDto>('/api/integrations/skills', { method: 'POST', ...j(d) }),
  updateSkill: (id: string, d: Record<string, unknown>) =>
    apiFetch<SkillBundleDto>(`/api/integrations/skills/${id}`, { method: 'PATCH', ...j(d) }),
  deleteSkill: (id: string) => apiFetch<void>(`/api/integrations/skills/${id}`, { method: 'DELETE' }),

  // ---- skill files (file-based skills) ----
  listSkillFiles: (id: string) => apiFetch<SkillFileDto[]>(`/api/integrations/skills/${id}/files`),
  uploadSkillFile: (id: string, d: { name: string; mimeType?: string; data: string }) =>
    apiFetch<SkillFileDto & { note?: string | null }>(`/api/integrations/skills/${id}/files`, { method: 'POST', ...j(d) }),
  deleteSkillFile: (id: string, fileId: string) =>
    apiFetch<void>(`/api/integrations/skills/${id}/files/${fileId}`, { method: 'DELETE' }),
  importSkill: (d: { markdown: string; name?: string }) =>
    apiFetch<SkillBundleDto & { removed: string[] }>('/api/integrations/skills/import', { method: 'POST', ...j(d) }),

  // ---- inbound webhooks (triggers) ----
  listInbound: () => apiFetch<InboundWebhookDto[]>('/api/integrations/inbound'),
  createInbound: (d: { agent_id: string; name?: string; rate_limit_per_min?: number; signing_secret?: string }) =>
    apiFetch<InboundCreated>('/api/integrations/inbound', { method: 'POST', ...j(d) }),
  updateInbound: (id: string, d: { enabled?: boolean; name?: string; agent_id?: string }) =>
    apiFetch<{ ok: boolean }>(`/api/integrations/inbound/${id}`, { method: 'PATCH', ...j(d) }),
  deleteInbound: (id: string) => apiFetch<void>(`/api/integrations/inbound/${id}`, { method: 'DELETE' }),

  // ---- per-agent enablement ----
  getAgentIntegrations: (agentId: string) =>
    apiFetch<AgentEnabled>(`/api/agents/${agentId}/integrations`),
  setAgentIntegrations: (agentId: string, kind: EnableKind, ids: string[]) =>
    apiFetch<AgentEnabled>(`/api/agents/${agentId}/integrations/${kind}`, { method: 'PUT', ...j({ ids }) }),

  // ---- Этап 1: каталог сервисов и подключения ----
  getCatalog: () => apiFetch<CatalogResponse>('/api/integrations/catalog'),
  listConnections: () => apiFetch<IntegrationConnectionDto[]>('/api/integrations/connections'),
  connectProvider: (provider: string) =>
    apiFetch<ConnectResult>(`/api/integrations/connect/${provider}`, { method: 'POST', body: JSON.stringify({ origin: 'tma' }) }),
  deleteConnection: (id: string) =>
    apiFetch<void>(`/api/integrations/connections/${id}`, { method: 'DELETE' }),
  getConnectionStatus: (provider: string, connectionId: string) =>
    apiFetch<{ status: string; connection?: IntegrationConnectionDto }>(`/api/integrations/connect/${provider}/status?connectionId=${connectionId}&wait=1`, { method: 'GET' }),
  getAgentConnections: (agentId: string) =>
    apiFetch<AgentConnections>(`/api/agents/${agentId}/connections`),
  setAgentConnections: (agentId: string, connectionIds: string[]) =>
    apiFetch<{ ok: boolean; enabledConnectionIds: string[] }>(`/api/agents/${agentId}/connections`, { method: 'PUT', ...j({ connectionIds }) }),
  testConnection: (id: string) =>
    apiFetch<{ ok: boolean; error?: string | null; demo?: boolean; tools: string[]; toolDetails?: McpToolInfo[] }>(`/api/integrations/connections/${id}/test`, { method: 'POST', body: '{}' }),
  connectToken: (provider: string, body: Record<string, string>) =>
    apiFetch<{ ok: boolean; connection: IntegrationConnectionDto }>(`/api/integrations/connect-token/${provider}`, { method: 'POST', ...j(body) }),
  updateAdvanced: (id: string, body: { advanced_api?: boolean; advanced_readonly?: boolean }) =>
    apiFetch<{ ok: boolean; advanced_api: boolean; advanced_readonly: boolean }>(`/api/integrations/connections/${id}/advanced`, { method: 'PATCH', ...j(body) }),

  // ---- Telegram (общая интеграция: платформенный бот + свой бот) ----
  tgOverview: () => apiFetch<TgOverview>('/api/integrations/telegram/overview'),
  tgConnectPlatform: () => apiFetch<TgConnectPlatform>('/api/integrations/telegram/connect-platform', { method: 'POST', body: '{}' }),
  tgConnectCustom: (token: string) => apiFetch<{ ok: boolean; connection: TgConnectionDto; botUsername: string }>('/api/integrations/telegram/connect-custom', { method: 'POST', ...j({ token }) }),
  tgDiscover: (connectionId: string) => apiFetch<{ ok: boolean; resources: TgResourceDto[]; hint: string | null }>(`/api/integrations/telegram/custom/${connectionId}/discover`, { method: 'POST', body: '{}' }),
  tgResources: () => apiFetch<TgResourceDto[]>('/api/integrations/telegram/resources'),
  tgConfirmResource: (id: string) => apiFetch<{ ok: boolean; resource: TgResourceDto | null }>(`/api/integrations/telegram/resources/${id}/confirm`, { method: 'POST', body: '{}' }),
  tgCheckResource: (id: string) => apiFetch<{ ok: boolean; resource: TgResourceDto | null }>(`/api/integrations/telegram/resources/${id}/check`, { method: 'POST', body: '{}' }),
  tgDeleteResource: (id: string) => apiFetch<void>(`/api/integrations/telegram/resources/${id}`, { method: 'DELETE' }),
  tgDeleteConnection: (id: string) => apiFetch<void>(`/api/integrations/telegram/connection/${id}`, { method: 'DELETE' }),
};
